// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { BasicUserInfo, useAuthContext } from "@asgardeo/auth-react";
import { FunctionComponent, PropsWithChildren, ReactElement, createContext, useEffect, useState } from "react";
import { ALLOWED_GROUPS, ADD_WORKNOTE_GROUPS, ADD_ESCALATION_GROUPS, DOWNLOAD_ATTACHMENT_GROUPS, USAGE_METRICS_GROUPS } from "../data/configs/spl-config";
import { useNavigate } from "react-router-dom";
import CircularLoading from "./Loading";

interface AuthorizePropsInterface {
}

type AuthInfoType = {
    isUserAllowedtoAddWorkNotes: boolean;
    isUserAllowedtoAddEscalations: boolean;
    isUserAllowedtoDownloadAttachments: boolean;
    isUserAllowedToViewUsageMetrics: boolean;
    basicUserInfo: BasicUserInfo;
};

export const PermissionContext = createContext<AuthInfoType>({} as AuthInfoType);

const AuthorizedApp: FunctionComponent<PropsWithChildren<AuthorizePropsInterface>> = (
    props: PropsWithChildren<AuthorizePropsInterface>
): ReactElement => {
    const { children } = props;
    const { getBasicUserInfo, state } = useAuthContext();
    const [basicUserInfo, setBasicUserInfo] = useState<BasicUserInfo>({} as BasicUserInfo);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        getBasicUserInfo()
            .then((userInfo) => {
                setBasicUserInfo(userInfo);
                if (!isUserBelongsToGroup(ALLOWED_GROUPS, userInfo)) {
                    navigate("/unauthorized");
                } else {
                    setIsAuthorized(true);
                }
            })
            .catch((error) => {
                console.error(error);
                navigate("/unauthorized");
            });
    }, [state.isAuthenticated]);

    const authInfo: AuthInfoType = {
        isUserAllowedtoAddWorkNotes: isUserBelongsToGroup(ADD_WORKNOTE_GROUPS, basicUserInfo),
        isUserAllowedtoAddEscalations: isUserBelongsToGroup(ADD_ESCALATION_GROUPS, basicUserInfo),
        isUserAllowedtoDownloadAttachments: isUserBelongsToGroup(DOWNLOAD_ATTACHMENT_GROUPS, basicUserInfo),
        isUserAllowedToViewUsageMetrics: isUserBelongsToGroup(USAGE_METRICS_GROUPS, basicUserInfo),
        basicUserInfo: basicUserInfo
    };

    return (
        <>
            {isAuthorized ? (
                <PermissionContext.Provider value={authInfo}>
                    {children}
                </PermissionContext.Provider>
            )
                : (<CircularLoading openLoading={!isAuthorized} />)
            }
        </>
    )
}

export default AuthorizedApp;

const isUserBelongsToGroup = (groups: string[], basicUserInfo: BasicUserInfo): boolean => {
    if (basicUserInfo.groups) {
        const userGroups = basicUserInfo.groups;
        for (let i = 0; i < groups.length; i++) {
            if (userGroups.includes(groups[i])) {
                return true;
            }
        }
    }
    return false;
}
